'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { getApiBaseUrl } from '@/lib/config'
import { getImageUrl, getMediaFileUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { extractYoutubeVideoId } from '@/lib/comment-embed-pipeline'
import { extractDailymotionVideoId, extractVimeoVideoId } from '@/lib/video-utils'
import { rewriteContentImageUrlsForDisplay } from '@/lib/process-article-content-images'
import { sanitizeArticleContentHtml } from '@/lib/sanitize-article-content-html'
import { sanitizePublicArticleHtml } from '@/lib/safe-html'
import { useNativeProviderEmbeds } from '@/lib/use-native-provider-embeds'
import { LiveBeaconIcon } from '@/components/LiveBadge'
import VideoPlayer from '@/components/VideoPlayer'
import { formatSiteDate, formatSiteTime } from '@/lib/site-datetime'
import { reconcileLiveHistory } from '@/lib/live-blog-history'

export type LiveUpdate = {
  id: number
  title?: string | null
  body: string
  image?: string | null
  video?: string | null
  createdAt: string
}

function stripHtml(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function looksLikeHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value)
}

function prepareLiveBodyHtml(body: string) {
  const trimmed = body.trim()
  if (!trimmed) return ''
  if (!looksLikeHtml(trimmed)) {
    return trimmed
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p>${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
      .join('')
  }
  return sanitizePublicArticleHtml(
    rewriteContentImageUrlsForDisplay(sanitizeArticleContentHtml(trimmed)),
  )
}

function listSignature(list: LiveUpdate[]) {
  return JSON.stringify(list.map(({ id, title, body, image, video, createdAt }) =>
    [id, title ?? null, body, image ?? null, video ?? null, createdAt]))
}

function formatClock(iso: string) {
  try {
    return formatSiteTime(iso, {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function isSameDay(a: string, b: string) {
  return (
    formatSiteDate(a, { year: 'numeric', month: '2-digit', day: '2-digit' }) ===
    formatSiteDate(b, { year: 'numeric', month: '2-digit', day: '2-digit' })
  )
}

function formatDayLabel(iso: string) {
  try {
    return formatSiteDate(iso, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  } catch {
    return ''
  }
}

function formatRelative(iso: string, nowMs: number) {
  const diff = Math.max(0, nowMs - new Date(iso).getTime())
  const sec = Math.floor(diff / 1000)
  if (sec < 45) return 'Az önce'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} dk önce`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} sa önce`
  return `${Math.floor(hr / 24)} gün önce`
}

function LiveVideoEmbed({ video }: { video: string }) {
  const yt = extractYoutubeVideoId(video)
  const dm = extractDailymotionVideoId(video)
  const vimeo = extractVimeoVideoId(video)
  const src = /^https?:\/\//i.test(video) ? video : getMediaFileUrl(video)
  if (!yt && !dm && !vimeo && !src) return null

  return (
    <div className="live-blog__media live-blog__media--video">
      <VideoPlayer
        video={!yt && !dm && !vimeo ? src : null}
        youtube={yt ? video : null}
        dailymotion={dm ? video : null}
        vimeo={vimeo ? video : null}
        title="Canlı blog video"
        className="!mb-0 !rounded-[5px] !shadow-none"
      />
    </div>
  )
}

function LiveBodyHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useNativeProviderEmbeds(ref, html)

  return (
    <div
      ref={ref}
      className="live-blog__text news-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

export default function LiveBlogUpdates({
  newsId,
  initialUpdates = [],
  active = true,
  onLiveChange,
  onImageChange,
}: {
  newsId: number
  initialUpdates?: LiveUpdate[]
  active?: boolean
  onLiveChange?: (active: boolean) => void
  onImageChange?: (image: string) => void
}) {
  const [updates, setUpdates] = useState<LiveUpdate[]>(() => reconcileLiveHistory([], initialUpdates, true))
  const [isActive, setIsActive] = useState(active)
  const onLiveChangeRef = useRef(onLiveChange)
  onLiveChangeRef.current = onLiveChange
  const onImageChangeRef = useRef(onImageChange)
  onImageChangeRef.current = onImageChange
  const [loading, setLoading] = useState(initialUpdates.length === 0)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [freshIds, setFreshIds] = useState<Set<number>>(() => new Set())
  const updatesRef = useRef(updates)
  const knownIdsRef = useRef<Set<number>>(new Set(updates.map((u) => u.id)))
  const lastSigRef = useRef(listSignature(updates))
  const freshTimerRef = useRef<number | null>(null)

  const applyList = useCallback((incoming: unknown, historyComplete: boolean) => {
    const list = reconcileLiveHistory(updatesRef.current, incoming, historyComplete)
    const sig = listSignature(list)
    if (sig === lastSigRef.current) return
    lastSigRef.current = sig

    const prev = knownIdsRef.current
    const arrived = list.filter((u) => !prev.has(u.id)).map((u) => u.id)
    if (arrived.length && prev.size > 0) {
      setFreshIds(new Set(arrived))
      if (freshTimerRef.current != null) window.clearTimeout(freshTimerRef.current)
      freshTimerRef.current = window.setTimeout(() => {
        freshTimerRef.current = null
        setFreshIds(new Set())
      }, 10_000)
    }
    updatesRef.current = list
    knownIdsRef.current = new Set(list.map((u) => u.id))
    setUpdates(list)
    setLoading(false)
  }, [])

  useEffect(() => {
    const list = reconcileLiveHistory([], initialUpdates, true)
    updatesRef.current = list
    knownIdsRef.current = new Set(list.map((u) => u.id))
    lastSigRef.current = listSignature(list)
    setUpdates(list)
    setLoading(initialUpdates.length === 0)
    setFreshIds(new Set())
    return () => {
      if (freshTimerRef.current != null) window.clearTimeout(freshTimerRef.current)
      freshTimerRef.current = null
    }
  }, [newsId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false
    let confirmedClosed = false
    let pollTimer: number | null = null
    let clockTimer: number | null = null
    let es: EventSource | null = null
    let streamVersion = 0
    let latestRequest = 0
    const controller = new AbortController()
    setIsActive(active)

    const stopTransport = () => {
      if (pollTimer) window.clearInterval(pollTimer)
      if (clockTimer) window.clearInterval(clockTimer)
      pollTimer = null
      clockTimer = null
      if (es) {
        es.onmessage = null
        es.onerror = null
        es.close()
        es = null
      }
    }
    const receive = (json: { data?: unknown; live?: unknown; image?: unknown; historyComplete?: unknown }) => {
      // A slow request started before the final SSE frame cannot reopen the feed
      // or replace its final entries with an older list.
      if (cancelled || confirmedClosed) return
      if (Array.isArray(json.data)) applyList(json.data, json.historyComplete === true)
      // A missing/null field from an older API must never erase the article photo.
      const image = typeof json.image === 'string' ? json.image.trim() : ''
      if (image && !/^(?:null|undefined)$/i.test(image)) onImageChangeRef.current?.(image)
      if (typeof json.live === 'boolean') {
        setIsActive(json.live)
        onLiveChangeRef.current?.(json.live)
        if (!json.live) {
          confirmedClosed = true
          stopTransport()
          controller.abort()
        }
      }
      setLoading(false)
    }
    const load = async () => {
      if (cancelled || confirmedClosed) return
      const streamAtStart = streamVersion
      const request = ++latestRequest
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/news/${newsId}/live-updates?all=1`, {
          cache: 'no-store', signal: controller.signal,
        })
        if (res.ok) {
          const json = await res.json()
          if (request === latestRequest && streamAtStart === streamVersion) receive(json)
        }
      } catch {
        /* Keep the last complete snapshot. */
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    const startPoll = (ms: number) => {
      if (cancelled || confirmedClosed || !active) return
      if (pollTimer) window.clearInterval(pollTimer)
      pollTimer = window.setInterval(() => {
        if (document.visibilityState !== 'hidden') void load()
      }, ms)
    }

    // Closed histories receive one final uncached snapshot, without opening a
    // stream or continuing to poll. The data field remains compatible with old APIs.
    void load()
    if (active) {
      try {
        es = new EventSource(`${getApiBaseUrl()}/api/news/${newsId}/live-updates/stream?all=1`)
        es.onmessage = (event) => {
          if (cancelled || confirmedClosed) return
          try {
            const json = JSON.parse(event.data)
            if (!json || typeof json !== 'object' || Array.isArray(json)) return
            streamVersion++
            receive(json)
          } catch { /* Ignore an incomplete frame. */ }
        }
        es.onerror = () => {
          es?.close()
          es = null
          if (!cancelled && !confirmedClosed) startPoll(4000)
        }
        startPoll(12_000)
      } catch {
        startPoll(4000)
      }
      clockTimer = window.setInterval(() => setNowMs(Date.now()), 20_000)
    }
    const onVis = () => {
      if (active && !confirmedClosed && document.visibilityState === 'visible') void load()
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      cancelled = true
      controller.abort()
      document.removeEventListener('visibilitychange', onVis)
      stopTransport()
    }
  }, [newsId, active, applyList])

  const latestAt = updates[0]?.createdAt
  const spansMultipleDays = useMemo(() => {
    if (updates.length < 2) return false
    const first = updates[0]?.createdAt
    return updates.some((u) => first && !isSameDay(first, u.createdAt))
  }, [updates])

  const rows = useMemo(() => {
    const out: Array<
      | { type: 'day'; key: string; label: string }
      | { type: 'item'; key: string; update: LiveUpdate; index: number }
    > = []
    updates.forEach((u, index) => {
      const prev = updates[index - 1]
      if (spansMultipleDays && (!prev || !isSameDay(prev.createdAt, u.createdAt))) {
        out.push({ type: 'day', key: `d-${u.createdAt.slice(0, 10)}`, label: formatDayLabel(u.createdAt) })
      }
      out.push({ type: 'item', key: `u-${u.id}`, update: u, index })
    })
    return out
  }, [updates, spansMultipleDays])

  return (
    <section className="live-blog" aria-label={isActive ? 'Canlı gelişmeler' : 'Haber güncellemeleri'}>
      <header className="live-blog__head">
        <div className="live-blog__head-main">
          <h2 className="live-blog__live-pill">
            {isActive ? <LiveBeaconIcon /> : null}
            Son Gelişmeler
          </h2>
        </div>
        <p className="live-blog__status">
          {!isActive
            ? 'Canlı akış kapalı'
            : loading && updates.length === 0
            ? 'Yükleniyor…'
            : latestAt
              ? formatRelative(latestAt, nowMs)
              : 'Bekleniyor'}
        </p>
      </header>

      {updates.length === 0 && !loading ? (
        <p className="live-blog__empty">
          {isActive
            ? 'Henüz güncelleme yok. Bu sayfa açık kaldıkça yeni gelişmeler burada görünür.'
            : 'Bu habere ait güncelleme bulunmuyor.'}
        </p>
      ) : (
        <>
          <ol className="live-blog__list">
            {rows.map((row) => {
              if (row.type === 'day') {
                return (
                  <li key={row.key} className="live-blog__day">
                    <span>{row.label}</span>
                  </li>
                )
              }
              const u = row.update
              const isFresh = freshIds.has(u.id)
              const isLatest = row.index === 0
              const imageUrl = u.image ? getImageUrl(u.image, IMAGE_PRESETS.detail) : null
              const bodyPlain = stripHtml(u.body || '')
              const showBody = Boolean(bodyPlain) && bodyPlain !== (u.title || '').trim()
              const bodyHtml = showBody ? prepareLiveBodyHtml(u.body || '') : ''

              return (
                <li
                  key={row.key}
                  id={`live-update-${u.id}`}
                  className={[
                    'live-blog__post',
                    isLatest ? 'live-blog__post--latest' : '',
                    isFresh ? 'live-blog__post--fresh' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <time dateTime={u.createdAt} className="live-blog__time">
                    {formatClock(u.createdAt)}
                  </time>
                  <div className="live-blog__content">
                    {isFresh ? <span className="live-blog__chip">Yeni</span> : null}
                    {u.title?.trim() ? (
                      <h3 className="live-blog__post-title">{u.title.trim()}</h3>
                    ) : null}
                    {bodyHtml ? <LiveBodyHtml html={bodyHtml} /> : null}
                    {imageUrl ? (
                      <div className="live-blog__media">
                        <Image
                          src={imageUrl}
                          alt={u.title?.trim() || 'Canlı güncelleme görseli'}
                          width={960}
                          height={540}
                          className="live-blog__img"
                          sizes="(max-width: 768px) 100vw, 720px"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    {u.video?.trim() ? <LiveVideoEmbed video={u.video.trim()} /> : null}
                  </div>
                </li>
              )
            })}
          </ol>
        </>
      )}
    </section>
  )
}
